import React, { Fragment, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { useElementDimensions } from "@sportal/cdk/hooks";

import { NotMobile } from "../../../hocs/Responsive";
import {
  barchartKeys,
  ReportTypes,
} from "../../../helpers/reports.helper.types";
import {
  isBarchartLoadFailed,
  getBarchartReports,
  isBarchartLoadSuccessful,
} from "../../../store/reports/reducers/barchart/barchart.selectors";
import {
  getReportsPeriod,
  getSelectedReportsProfile,
} from "../../../store/reports/reports.selectors";
import {
  BarChartProvider,
  BarChartTooltip,
  Bars,
  ChartNoDataMessage,
  useBarChartContext,
  useBarchartTooltipCleanup,
  XAxis,
  yLinearScale,
  yPowScale,
} from "../../../components/charts/barChart";
import { LoadError } from "../../../components/charts/LoadError";
import { loadBarChartReport } from "../../../store/reports/reducers/barchart/barchart.actions";

const BOTTOM_CHART_HEIGHT = 60;

export const CHART_SETTINGS = {
  barTypes: [ReportTypes.Activity, ReportTypes.Webfilter, ReportTypes.Malware],
  dataSelection: {
    top: (d) => d[ReportTypes.Activity] || d[ReportTypes.Webfilter],
    bottom: (d) => d[ReportTypes.Malware],
  },
  [ReportTypes.Activity]: {
    yOffsets: (val) => Math.abs(yPowScale(val)) || 0,
    getHeight: (val) => Math.abs(yPowScale(val) - yPowScale(0)) || 0,
  },
  [ReportTypes.Webfilter]: {
    yOffsets: (val) => Math.abs(yPowScale(val)) || 0,
    getHeight: (val) => Math.abs(yPowScale(val) - yPowScale(0)) || 0,
  },
  [ReportTypes.Malware]: {
    yOffsets: () => 176,
    getHeight: (val) => Math.abs(yLinearScale(val) - yLinearScale(0)) || 0,
  },
};

export const HomePageBarChart = () => {
  const dispatch = useDispatch();
  const container = useRef(null);
  const rawData = useSelector(getBarchartReports);
  const period = useSelector(getReportsPeriod);
  const profile = useSelector(getSelectedReportsProfile);
  const isFailure = useSelector(isBarchartLoadFailed);
  const { width, height } = useElementDimensions(container);

  const fetchBarchartReport = useCallback(() => {
    dispatch(loadBarChartReport(barchartKeys, { period, profile }));
  }, [dispatch, period, profile]);

  useEffect(() => {
    fetchBarchartReport();
  }, [fetchBarchartReport]);

  useBarchartTooltipCleanup();

  return (
    <div className={"scrollable-container"}>
      <div className={"width-container"}>
        <div
          className="home-page__bar-chart barchart-container"
          ref={container}
        >
          {isFailure && (
            <LoadError
              onRefreshClick={fetchBarchartReport}
              className={"bar-chart-failure-container"}
              style={{ height: `calc(100% - ${BOTTOM_CHART_HEIGHT}px)` }}
            />
          )}
          <BarChartProvider
            chartWidth={width}
            chartHeight={height}
            bottomChartHeight={BOTTOM_CHART_HEIGHT}
            chartSettings={CHART_SETTINGS}
            period={period}
            data={rawData}
          >
            <BarChart container={container} />
          </BarChartProvider>
        </div>
      </div>
    </div>
  );
};

const BarChart = ({ container }) => {
  const { data } = useBarChartContext();
  const isSuccess = useSelector(isBarchartLoadSuccessful);

  const labelId = "dashboardBarChartLabelId";

  return (
    <div>
      <svg role="img" aria-describedby={labelId} focusable="false">
        <title>
          <FormattedMessage id={"dashboard_bar_chart_title"} />
        </title>

        <XAxis />
        {isSuccess &&
          (!_.isEmpty(data) ? (
            <Fragment>
              <Bars data={data} type={ReportTypes.Activity} />
              <Bars data={data} type={ReportTypes.Webfilter} />
              <Bars data={data} type={ReportTypes.Malware} />
            </Fragment>
          ) : (
            <ChartNoDataMessage />
          ))}
      </svg>
      <NotMobile>
        <BarChartTooltip container={container} />
      </NotMobile>
      <div aria-hidden={true} className="sr-only" id={labelId}>
        <FormattedMessage id={"dashboard_bar_chart_label"} />
      </div>
    </div>
  );
};
